// @ts-nocheck
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import DOMPurify from 'dompurify';
import { logger } from '@/lib/logger';

interface ScriptSettings {
  google_analytics_id?: string;
  google_tag_manager_id?: string;
  meta_pixel_id?: string;
  head_scripts?: string;
  body_start_scripts?: string;
  body_end_scripts?: string;
}

// Routes where tracking scripts should NOT be loaded
const EXCLUDED_ROUTES = ['/app', '/admin', '/login', '/signup'];

// Strict regex patterns for tracking ID validation
const GA_ID_REGEX = /^G-[A-Z0-9]{10,12}$/;
const GTM_ID_REGEX = /^GTM-[A-Z0-9]{6,8}$/;
const PIXEL_ID_REGEX = /^[0-9]{15,16}$/;

/**
 * Validates Google Analytics ID format
 */
const isValidGAId = (id: string): boolean => {
  return GA_ID_REGEX.test(id);
};

/**
 * Validates Google Tag Manager ID format
 */
const isValidGTMId = (id: string): boolean => {
  return GTM_ID_REGEX.test(id);
};

/**
 * Validates Meta Pixel ID format
 */
const isValidPixelId = (id: string): boolean => {
  return PIXEL_ID_REGEX.test(id);
};

const isExcludedRoute = (pathname: string): boolean => {
  return EXCLUDED_ROUTES.some(route => pathname.startsWith(route));
};

export const useScriptInjection = () => {
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Don't inject scripts on system pages (app, admin, login, signup)
    if (isExcludedRoute(location.pathname)) {
      logger.debug('Script injection skipped for system route', location.pathname);
      return;
    }
    const injectScripts = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('key, value');

        if (error) {
          logger.error('Error fetching site settings', error);
          return;
        }

        const settings: ScriptSettings = {};
        data?.forEach((item: { key: string; value: string | null }) => {
          if (item.value) {
            (settings as Record<string, string>)[item.key] = item.value;
          }
        });

        // Inject Google Analytics with strict validation
        if (settings.google_analytics_id) {
          const gaId = settings.google_analytics_id.trim();
          
          if (!isValidGAId(gaId)) {
            logger.warn('Invalid Google Analytics ID format');
          } else if (!document.querySelector(`script[src*="gtag/js?id=${gaId}"]`)) {
            // Create gtag script (external src - safe)
            const gtagScript = document.createElement('script');
            gtagScript.async = true;
            gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
            document.head.appendChild(gtagScript);

            // Create inline gtag config using textContent (safer than innerHTML)
            const gtagConfigScript = document.createElement('script');
            gtagConfigScript.textContent = `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${gaId}');
            `;
            document.head.appendChild(gtagConfigScript);
            logger.debug('Google Analytics loaded');
          }
        }

        // Inject Google Tag Manager with strict validation
        if (settings.google_tag_manager_id) {
          const gtmId = settings.google_tag_manager_id.trim();
          
          if (!isValidGTMId(gtmId)) {
            logger.warn('Invalid Google Tag Manager ID format');
          } else if (!document.querySelector(`script[src*="gtm.js?id=${gtmId}"]`)) {
            // Head script using textContent
            const gtmScript = document.createElement('script');
            gtmScript.textContent = `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${gtmId}');
            `;
            document.head.appendChild(gtmScript);

            // Body noscript fallback - sanitize the URL to prevent injection
            const gtmNoscript = document.createElement('noscript');
            const iframe = document.createElement('iframe');
            iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
            iframe.height = '0';
            iframe.width = '0';
            iframe.style.display = 'none';
            iframe.style.visibility = 'hidden';
            gtmNoscript.appendChild(iframe);
            document.body.insertBefore(gtmNoscript, document.body.firstChild);
            logger.debug('Google Tag Manager loaded');
          }
        }

        // Inject Meta Pixel with strict validation
        if (settings.meta_pixel_id) {
          const pixelId = settings.meta_pixel_id.trim();
          
          if (!isValidPixelId(pixelId)) {
            logger.warn('Invalid Meta Pixel ID format');
          } else if (!window.fbq) {
            const metaScript = document.createElement('script');
            metaScript.textContent = `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${pixelId}');
              fbq('track', 'PageView');
            `;
            document.head.appendChild(metaScript);
            logger.debug('Meta Pixel loaded');
          }
        }

        // Inject custom head scripts with DOMPurify sanitization
        if (settings.head_scripts) {
          injectCustomScripts(settings.head_scripts, 'head');
        }

        // Inject custom body start scripts with DOMPurify sanitization
        if (settings.body_start_scripts) {
          injectCustomScripts(settings.body_start_scripts, 'body-start');
        }

        // Inject custom body end scripts with DOMPurify sanitization
        if (settings.body_end_scripts) {
          injectCustomScripts(settings.body_end_scripts, 'body-end');
        }

        setLoaded(true);
      } catch (error) {
        logger.error('Error in script injection', error);
      }
    };

    injectScripts();
  }, []);

  return loaded;
};

// Helper function to inject custom scripts with sanitization
const injectCustomScripts = (html: string, position: 'head' | 'body-start' | 'body-end') => {
  const marker = `data-custom-script-${position}`;
  
  // Don't inject if already exists
  if (document.querySelector(`[${marker}]`)) {
    return;
  }

  // Sanitize HTML with DOMPurify - allow script tags but sanitize content
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ADD_TAGS: ['script', 'noscript', 'iframe'],
    ADD_ATTR: ['src', 'async', 'defer', 'type', 'id', 'class'],
    FORCE_BODY: true,
  });

  // Create a temporary container to parse the sanitized HTML
  const temp = document.createElement('div');
  temp.innerHTML = sanitizedHtml;

  // Process script tags
  const scripts = temp.querySelectorAll('script');
  scripts.forEach((script) => {
    const newScript = document.createElement('script');
    newScript.setAttribute(marker, 'true');
    
    // Copy allowed attributes only
    const allowedAttrs = ['src', 'async', 'defer', 'type', 'id', 'class'];
    Array.from(script.attributes).forEach((attr) => {
      if (allowedAttrs.includes(attr.name)) {
        newScript.setAttribute(attr.name, attr.value);
      }
    });
    
    // Copy content using textContent for safety
    if (script.textContent) {
      newScript.textContent = script.textContent;
    }

    // Insert in correct position
    if (position === 'head') {
      document.head.appendChild(newScript);
    } else if (position === 'body-start') {
      document.body.insertBefore(newScript, document.body.firstChild);
    } else {
      document.body.appendChild(newScript);
    }
  });

  // Process noscript tags
  const noscripts = temp.querySelectorAll('noscript');
  noscripts.forEach((noscript) => {
    const newNoscript = document.createElement('noscript');
    newNoscript.setAttribute(marker, 'true');
    // Sanitize noscript content as well
    newNoscript.innerHTML = DOMPurify.sanitize(noscript.innerHTML);

    if (position === 'head') {
      document.head.appendChild(newNoscript);
    } else if (position === 'body-start') {
      document.body.insertBefore(newNoscript, document.body.firstChild);
    } else {
      document.body.appendChild(newNoscript);
    }
  });

  logger.debug(`Custom scripts injected at ${position}`);
};

// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: any;
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export default useScriptInjection;
